import { useState } from 'react'
import { AlertCircle, Eye, FileBarChart2 } from 'lucide-react'
import {
  TableRowActionsMenu,
  TableRowActionsMenuItem,
} from '@/components/common/TableRowActionsMenu'
import { EmptyState } from '@/components/common/EmptyState'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
} from '@/components/common/DataTable'
import { Badge, Button } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { formatDateTime } from '@/lib/formatters'
import type { ComplianceReportRow } from '@/features/reports/types/compliance-report.types'

function complianceVariant(
  status: ComplianceReportRow['complianceStatus']
): 'success' | 'warning' | 'danger' {
  if (status === 'compliant') return 'success'
  if (status === 'needs_attention') return 'warning'
  return 'danger'
}

function companyStatusVariant(
  status: ComplianceReportRow['companyStatus']
): 'success' | 'neutral' {
  return status === 'active' ? 'success' : 'neutral'
}

function formatStatusLabel(value: string): string {
  return value.replace(/_/g, ' ')
}

export interface ComplianceReportTableProps {
  reports: ComplianceReportRow[]
  isLoading: boolean
  error: Error | null
  hasAccess: boolean
  onRetry: () => void
  onViewDetails: (report: ComplianceReportRow) => void
}

export function ComplianceReportTable({
  reports,
  isLoading,
  error,
  hasAccess,
  onRetry,
  onViewDetails,
}: ComplianceReportTableProps): React.ReactElement {
  const [openRowId, setOpenRowId] = useState<string | null>(null)

  if (!hasAccess) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Reports access unavailable"
        description="This page could not load because the reports query was permission-gated."
      />
    )
  }

  if (isLoading) {
    return (
      <SectionLoader variant="table" cols={7} rows={6} label="Loading compliance reports" />
    )
  }

  if (error) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Unable to load compliance reports"
        description={error.message}
        action={
          <Button type="button" variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    )
  }

  if (reports.length === 0) {
    return (
      <EmptyState
        icon={FileBarChart2}
        title="No companies match these filters"
        description="Try broadening the date range or clearing one or more filters."
      />
    )
  }

  return (
    <DataTable caption="Compliance reports">
      <DataTableHead>
        <tr>
          <DataTableHeadCell>Company</DataTableHeadCell>
          <DataTableHeadCell>Compliance status</DataTableHeadCell>
          <DataTableHeadCell align="right">Score</DataTableHeadCell>
          <DataTableHeadCell align="right">Active members</DataTableHeadCell>
          <DataTableHeadCell align="right">Pending approvals</DataTableHeadCell>
          <DataTableHeadCell align="right">Open payroll runs</DataTableHeadCell>
          <DataTableHeadCell align="right">Payroll errors</DataTableHeadCell>
          <DataTableHeadCell align="right">Audit events</DataTableHeadCell>
          <DataTableHeadCell>Last audit</DataTableHeadCell>
          <DataTableHeadCell align="right">Documents</DataTableHeadCell>
          <DataTableHeadCell align="right">Actions</DataTableHeadCell>
        </tr>
      </DataTableHead>
      <DataTableBody>
        {reports.map((report) => (
          <DataTableRow
            key={report.companyId}
            className="cursor-pointer"
            onClick={() => onViewDetails(report)}
          >
            <DataTableCell noWrap={false}>
              <div className="min-w-[180px]">
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: report.companyColor ?? '#3b82f6' }}
                    aria-hidden
                  />
                  <span className="font-medium">{report.companyName}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  {report.companyCode ? <span>{report.companyCode}</span> : null}
                  <span>{report.companySlug}</span>
                  <Badge variant={companyStatusVariant(report.companyStatus)} className="capitalize">
                    {report.companyStatus}
                  </Badge>
                </div>
              </div>
            </DataTableCell>
            <DataTableCell>
              <Badge variant={complianceVariant(report.complianceStatus)} className="capitalize">
                {formatStatusLabel(report.complianceStatus)}
              </Badge>
            </DataTableCell>
            <DataTableCell align="right">{report.complianceScore}</DataTableCell>
            <DataTableCell align="right">{report.metrics.activeMembers}</DataTableCell>
            <DataTableCell align="right">{report.metrics.pendingApprovals}</DataTableCell>
            <DataTableCell align="right">{report.metrics.openPayrollRuns}</DataTableCell>
            <DataTableCell align="right">{report.metrics.payrollRunsWithErrors}</DataTableCell>
            <DataTableCell align="right">{report.metrics.totalAuditEvents}</DataTableCell>
            <DataTableCell>
              {report.metrics.lastAuditAt
                ? formatDateTime(report.metrics.lastAuditAt, 'dd MMM yyyy')
                : 'N/A'}
            </DataTableCell>
            <DataTableCell align="right">{report.metrics.totalDocuments}</DataTableCell>
            <DataTableCell
              align="right"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <TableRowActionsMenu
                rowId={report.companyId}
                openRowId={openRowId}
                onOpenRowIdChange={setOpenRowId}
                triggerAriaLabel={`Actions for ${report.companyName}`}
                menuAriaLabel="Report actions"
              >
                <TableRowActionsMenuItem
                  icon={<Eye className="h-4 w-4" />}
                  onSelect={() => onViewDetails(report)}
                >
                  View details
                </TableRowActionsMenuItem>
              </TableRowActionsMenu>
            </DataTableCell>
          </DataTableRow>
        ))}
      </DataTableBody>
    </DataTable>
  )
}

ComplianceReportTable.displayName = 'ComplianceReportTable'
